<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <app-date-picker
          @input="onChangeFilter"
          v-model="filter_start"
          placeholder="From"
          class="datepicker--filter"
        />
        <app-date-picker
          @input="onChangeFilter"
          v-model="filter_end"
          placeholder="To"
          class="datepicker--filter"
        />
        <app-select
          @input="onChangeCategory"
          v-model="category_filter"
          :options="category_options"
          :reduce="item => item.id"
          :filterable="false"
          :searchable="false"
          placeholder="Category"
          class="select--200"
        />
        <app-select
          v-if="can_filter_by_manager"
          @input="onChangeCategory"
          v-model="manager_filter"
          :options="manager_options"
          :reduce="item => item.id"
          :display-field="option => `${option.first_name} ${option.last_name}`"
          label="first_name"
          placeholder="Manager"
          class="select--200"
        />
      </app-cells>
      <div v-for="table_item of climbing_table" :key="table_item.name" class="climbing-block">
        <app-cells v-if="table_item.name">
          <h2 class="title">{{ table_item.name }}</h2>
          <div v-if="table_item.today" class="status-title" :style="{ 'backgroundColor': '#77b017' }">Today</div>
        </app-cells>
        <v-client-table
          :data="table_item.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader }"
        >
          <template #h__name><div class="table-default__left">Name</div></template>
          <template #h__time_start><div class="table-default__left">Time & Location</div></template>
          <template #name="{ row }">
            <div class="table-default__left table-default__controls">
              <span
                v-tooltip="{ content: row.notes, placement: 'top-end', offset: 10 }"
                class="table-default__activity table-default__activity--notes"
                :class="{'table-default__activity--active': row.notes}"
              ></span>
              <router-link
                :to="{ name: 'climbing-briefings-detail-clients', params: { id: row.id } }"
                class="table-default__link-border"
              >
                {{ row.name }}
              </router-link>
            </div>
          </template>
          <template #time_start="{ row }">
            <div class="table-default__controls table-default__left">
              <span
                class="table-default__activity table-default__activity--briefing table-default__activity--no-events"
                :class="{'table-default__activity--active': row.briefing}"
              ></span>
              <span
                class="table-default__activity table-default__activity--equipment_check table-default__activity--no-events"
                :class="{'table-default__activity--active': row.eq_check}"
              ></span>
              {{ row.time_start }} at
              <span v-if="row.location.place_id !== 'ChIJqQRrDoleNxgRANW_07pTSXY'" class="status-tag" :style="{ 'backgroundColor': '#439ad1' }">{{ row.location.short_name }}</span>
              <span v-else>{{ row.location.short_name }}</span>
            </div>
          </template>
          <template #invoices_count="{ row }">
            <div class="table-default__controls">
              <span
                v-if="row.invoices_count"
                class="status-tag"
                :style="{ 'backgroundColor': row.invoices_count === row.invoices_printed_count ? '#77b017' : '#f0c427' }"
              >
                {{ row.invoices_printed_count }} / {{ row.invoices_count }}
              </span>
              <span
                v-if="row.invoices_unpaid"
                v-tooltip="{ content: 'Unpaid (clarify charge method with sales manager)', placement: 'top-start', offset: 10 }"
                class="status-tag"
                :style="{ 'backgroundColor': '#e85c20' }"
              >
                {{ row.invoices_unpaid }}
              </span>
              <span v-if="!row.invoices_count && !row.invoices_unpaid">-</span>
            </div>
          </template>
          <template #managers="{ row }">
            <div class="table-default__controls">
              <div v-for="manager of row.managers" :key="manager.id" class="table-default__photo">
                <img
                  :src="getUserAvatar(manager.crop.crop_url)" alt="Avatar"
                  v-tooltip="{ content: manager.full_name, placement: 'top-start', offset: 10 }"
                >
              </div>
            </div>
          </template>
          <template #speech="{ row }">
            <div v-if="row.speech" class="table-default__controls">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.speech.crop.crop_url)" alt="Avatar"
                  v-tooltip="{ content: row.speech.full_name, placement: 'top-start', offset: 10 }"
                >
              </div>
            </div>
            <span v-else>-</span>
          </template>
          <template #created_by="{ row }">
            <div class="table-default__controls">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.created_by?.crop?.crop_url)" alt="Avatar"
                  v-tooltip="{ content: `${row.created_by?.full_name || ''}`, placement: 'top-start', offset: 10 }"
                >
              </div>
              {{ dateTimeFormat(row.created_at) }}
            </div>
          </template>
        </v-client-table>
      </div>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table_length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getClimbingBriefingsByDates, getUsers } from '@/http'
import debounce from 'lodash/debounce'
import { format, parse, parseISO } from 'date-fns'

export default {
  name: 'ClimbingBriefings',
  data() {
    return {
      is_loaded: false,
      is_create: true,
      table_length: 0,
      climbing_table: [],
      table: {
        items: [],
        columns: [
          'name',
          'time_start',
          'invoices_count',
          'speech',
          'managers',
          'created_by',
        ],
        options: {
          headings: {
            'name': 'Name',
            'time_start': 'Time & Location',
            'invoices_count': 'Invoices',
            'speech': 'Speech',
            'managers': 'Managers',
            'created_by': 'Created by',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form: {},
      routes_options: [],
      group_type_options: [],
      category_filter: 'upcoming',
      category_options: [
        {
          id: 'upcoming',
          name: 'Upcoming',
        },
        {
          id: 'past',
          name: 'Past',
        },
      ],
      filter_start: null,
      filter_end: null,
      manager_filter: '',
      manager_options: [],
      table_preloader: false
    }
  },
  computed: {
    can_filter_by_manager() {
      return this.$store.state.userGroups.some(item => ['general_manager', 'operations_manager', 'operations_supervisor'].includes(item.code))
    },
  },
  created() {
    this.fetchItems()
    getUsers({ group: 'operations_manager,operations_supervisor', is_active: true }).then(response => { this.manager_options = response.data })
  },
  methods: {
    clientTableList(clients) {
      let result = ''
      for (const key in clients) {
        result += `${clients[key].first_name} ${clients[key].last_name}<br>`
      }
      return result
    },
    onChangeFilter() {
      this.category_filter = ''
      this.manager_filter = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onChangeCategory() {
      this.pagination.page = 1
      this.fetchItems()
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    fetchItems() {
      getClimbingBriefingsByDates({
        page: this.pagination.page,
        page_size: 20,
        date_range_after: this.filter_start,
        date_range_before: this.filter_end,
        category: this.category_filter,
        group_by: 'date',
        manager: this.manager_filter,
        range_type: 'day'
      }).then(res => {
        this.climbing_table = res.data.results.length ? res.data.results : [{name: '', items: []}]
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        this.table_length = 0
        res.data.results.forEach(item => {
          this.table_length += item.items.length
        })
        this.is_loaded = true
        this.table_preloader = false
      })
    },
    onGroupsSearch() {
      this.searchGroups(this)
    },
    searchGroups: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>

<style lang="sass" scoped>
.climbing-block
  margin-bottom: 30px
</style>
